import {
  type LocalDate,
  type TaxPeriod,
  addDays,
  addMonths,
  crEndOfDay,
  crInstant,
  daysInMonth,
  localDate,
  parseLocalDate,
  periodEnd,
} from "@despejo/domain";
import type { BusinessCalendar } from "./calendar.js";
import type { DeadlineRule, PaymentRule } from "./types.js";

export interface ComputedDeadline {
  /** The date the rule gives before business-day adjustment. */
  nominal: LocalDate;
  /** The legal due date after adjustment (what Hacienda enforces). */
  legal: LocalDate;
}

function dayIn(y: number, m: number, day: number | "LAST"): LocalDate {
  const last = daysInMonth(y, m);
  if (day === "LAST") return localDate(y, m, last);
  if (day < 1 || day > 31) throw new RangeError(`bad day ${day}`);
  return localDate(y, m, Math.min(day, last));
}

/** Compute the due date for a single (non-installment) period. */
export function computeDeadline(rule: DeadlineRule, period: TaxPeriod, cal: BusinessCalendar): ComputedDeadline {
  return computeDeadlineFromEnd(rule, periodEnd(period), cal);
}

/**
 * Same as `computeDeadline`, for a period given by its last day: every rule
 * type is relative to the period's end (fiscal periods that are not calendar
 * years, DES-103).
 */
export function computeDeadlineFromEnd(rule: DeadlineRule, end: LocalDate, cal: BusinessCalendar): ComputedDeadline {
  switch (rule.type) {
    case "DAY_OF_FOLLOWING_PERIOD": {
      const monthStart = addMonths(localDate(parseLocalDate(end).y, parseLocalDate(end).m, 1), rule.monthsAfter ?? 1);
      const { y, m } = parseLocalDate(monthStart);
      if (rule.businessDays) {
        if (rule.day === "LAST") throw new RangeError("LAST is not valid with businessDays");
        const legal = cal.nthBusinessDay(monthStart, rule.day);
        return { nominal: legal, legal };
      }
      const nominal = dayIn(y, m, rule.day);
      return { nominal, legal: cal.adjust(nominal, rule.adjustment ?? "NEXT_BUSINESS_DAY") };
    }
    case "FIXED_DATE": {
      const { y } = parseLocalDate(end);
      const nominal = dayIn(y + rule.yearOffset, rule.month, rule.day);
      return { nominal, legal: cal.adjust(nominal, rule.adjustment ?? "NEXT_BUSINESS_DAY") };
    }
    case "DAYS_AFTER_PERIOD_END": {
      if (rule.businessDays) {
        const legal = cal.addBusinessDays(end, rule.days);
        return { nominal: legal, legal };
      }
      const nominal = addDays(end, rule.days);
      return { nominal, legal: cal.adjust(nominal, rule.adjustment ?? "NEXT_BUSINESS_DAY") };
    }
    case "INSTALLMENTS":
      throw new TypeError("INSTALLMENTS rules produce several deadlines; use computeInstallments");
  }
}

export function computeInstallments(
  rule: Extract<DeadlineRule, { type: "INSTALLMENTS" }>,
  fiscalYear: number,
  cal: BusinessCalendar,
): { key: string; nominal: LocalDate; legal: LocalDate }[] {
  return rule.installments.map((i) => {
    const nominal = dayIn(fiscalYear + (i.yearOffset ?? 0), i.month, i.day);
    return { key: i.key, nominal, legal: cal.adjust(nominal, rule.adjustment ?? "NEXT_BUSINESS_DAY") };
  });
}

/** Deadline instant: the last millisecond of the legal due date in CR time. */
export function dueAt(legal: LocalDate): string {
  return crEndOfDay(legal).toISOString();
}

/** Advisory "act before" instant, e.g. 18:00 CR on the due date for bank cut-offs. */
export function recommendedActionAt(legal: LocalDate, payment: PaymentRule | undefined): string | null {
  if (!payment?.recommended_cutoff_local) return null;
  return crInstant(legal, payment.recommended_cutoff_local).toISOString();
}
